import { isEmpty, result } from 'underscore';
import { assign } from 'xstate';
import Promise from 'bluebird';
import { withScope } from '@bingads-webui-react/scope-service';
import { stepStateMachine } from '@bingads-webui-campaign-react/step-wizard-machine';
import { saveAds, modelToViewModelForAd, getErrorsPlaceholders } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';

import { STEP_PATHS, viewModelToModelForCampaign } from '@bingads-webui-campaign-react/xandr-campaign';

import { StepAdsSettings } from './step-ads-settings';


const needsLoading = () => true;
const hasCampaignData = context =>
  Boolean(result(context, ['stepsData', 'campaign', 'Id']));

/**
 * Loads step data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 *
 * The function is called when the wizard enters the step.
 * The step will be blocked until the promise resolves.
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 */
const loadData = (context) => {
  const loadPromises = {
    ads: Promise.resolve(context.data.ads),
  };

  return Promise.props(loadPromises).then(({
    ads,
  }) => {
    const { deal } = context.stepsData.adGroup;
    const adsModel = modelToViewModelForAd({ adsModel: ads });

    const results = {
      ads: adsModel, oldAdsModel: adsModel, deal, ...getErrorsPlaceholders(),
    };

    return results;
  });
};

// must return `Promise.resolve(data)` or `Promise.reject(error)`
// the payload will be available in `onDone` and `onError` in event's `data` prop.
const saveData = context =>
  saveAds({
    ads: context.data.ads,
    oldAdsModel: context.data.oldAdsModel,
    campaign: context.stepsData.campaign,
    adGroup: context.stepsData.adGroup,
    activity: context.data.activity,
  }, {
    scenario: context.stepsData.scenarioName,
    activityLogger: context.stepsData.scenario.createActivity('saveAds'),
    i18n: context.stepsData.i18n,
  });

const isValid = context => context.data.ads.isValid;
const isDirty = context => context.data.ads.isDirty;
const hasServerErrors = context => !isEmpty(context.data.serverErrors) || !isEmpty(context.data.systemErrors);
export const needsSaving = context => isDirty(context) || hasServerErrors(context);

const patchStepsData = assign({
  stepsData: (context/* , event */) => ({
    ...context.stepsData,
    ads: context.data.ads,
    ...hasCampaignData(context) ? {} :
      { campaign: viewModelToModelForCampaign(context.data.campaign) },
  }),
});
const clearErrors = assign({
  data: context => ({
    ...context.data,
    ...getErrorsPlaceholders(),
  }),
});

export const stepAdsWizardConfig = {
  id: STEP_PATHS.ADS,
  title: _TL_('Ads'),
  StepMachine: stepStateMachine,
  StepMachineOptions: (stepInfo, initialStepsData) => ({
    machineId: STEP_PATHS.ADS,
    initialContext: {
      id: stepInfo.id,
      stepInfo,
      stepsData: initialStepsData,
      data: {
        ads: [],
        ...getErrorsPlaceholders(),
      },
    },
    actions: {
      patchStepsData,
      clearErrors,
    },
    guards: {
      needsLoading,
      needsSaving,
      isValid,
      isDirty,
    },
  }),
  StepComponent: withScope(StepAdsSettings),
  canNavigateBackWithoutSaving: true,
  stepRepositoryConfig: {
    saveData,
    loadData,
  },
};
